/**
 * Extend the axios API layer added in Phase 1 with Phase-2 endpoints.
 * Kept in its own module so `lib/api.js` doesn't accumulate more state.
 */
import axios from "axios";
import { API_BASE } from "@/lib/api";
import { ingestServerDateHeader } from "@/lib/clock-sync";

// A separate axios instance so the Phase-1 auth interceptor doesn't get
// in the way of these public endpoints. Same base URL and timeout.
const raw = axios.create({ baseURL: API_BASE, timeout: 15_000 });

// Feed responses from THIS axios instance into the clock-sync
// estimator too — most of the app's polling happens on the stats-api
// client, so this is the primary offset source in practice.
raw.interceptors.request.use((config) => {
  config.metadata = { ...(config.metadata || {}), startMs: Date.now() };
  return config;
});
raw.interceptors.response.use((r) => {
  try {
    const startMs = r?.config?.metadata?.startMs;
    const dateHeader =
      r?.headers?.date || (r?.headers?.get && r.headers.get("date"));
    if (dateHeader) ingestServerDateHeader(dateHeader, startMs);
  } catch {
    /* best-effort */
  }
  return r;
});

export const statsApi = {
  history: (days = 30) =>
    raw.get(`/history?days=${days}`).then((r) => r.data),
  stats: () => raw.get(`/stats`).then((r) => r.data),
  statsMonthly: (month) =>
    raw
      .get(`/stats/monthly`, { params: month ? { month } : {} })
      .then((r) => r.data),
  exportJsonUrl: () => `${API_BASE}/export/json`,
  exportCsvUrl: () => `${API_BASE}/export/csv`,
  exportEarningsCsvUrl: () => `${API_BASE}/export/earnings.csv`,
  getSettings: () => raw.get(`/settings`).then((r) => r.data),
  putSettings: (payload) =>
    raw.put(`/settings`, payload).then((r) => r.data),

  // --- Phase 3 · earnings --------------------------------------------------
  listEarnings: (linkId) =>
    raw.get(`/links/${linkId}/earnings`).then((r) => r.data),
  addEarning: (linkId, payload) =>
    raw.post(`/links/${linkId}/earnings`, payload).then((r) => r.data),
  deleteEarning: (earningId) =>
    raw.delete(`/earnings/${earningId}`).then((r) => r.data),
  earningsSummary: () => raw.get(`/earnings/summary`).then((r) => r.data),

  // --- Phase 4 · telegram --------------------------------------------------
  telegramInfo: () => raw.get(`/telegram/info`).then((r) => r.data),

  // --- Phase 7 · 2-way sync status -----------------------------------------
  // Returns Mongo + Supabase row counts, outbox pending, last mirror ts,
  // and the overall health color. Safe to poll from the Settings UI.
  getSyncStatus: () => raw.get(`/sync/status`).then((r) => r.data),

  // Manually trigger the count-based bidirectional reconciler. Only
  // pushes/pulls the collections that have actually drifted — safe to
  // click repeatedly. Returns the reconcile summary.
  syncReconcile: () => raw.post(`/sync/reconcile`).then((r) => r.data),

  // --- Phase 8 · self-host zip builder --------------------------------
  // Returns {clean, with_env, with_env_token} with last-build info per
  // variant. UI uses this to show "last built X min ago" chips.
  selfhostStatus: () => raw.get(`/selfhost/status`).then((r) => r.data),

  // --- Phase 6 · danger-zone maintenance -----------------------------------
  clearMonth: () =>
    raw
      .post(`/maintenance/clear-month`, null, { params: { confirm: true } })
      .then((r) => r.data),
};
